<template>
  <Component :is="isLink ? 'RouterLink' : 'span'" :to="to">
    <div>
      <div
        class="order-item-container"
        data-testid="order-item"
        :class="{ expired: !order.isHiring }"
      >
        <div class="order-info">
          <div class="order-limit-and-service-tags" v-if="isShownTag">
            <Tag
              v-if="!!orderTypeLabel"
              variant="contained"
              class="small-tag order-limit-tag"
              :label="orderTypeLabel"
            />
            <Tag
              variant="contained"
              class="small-tag order-service-tag"
              :label="serviceLabel"
              data-testid="service-tag"
            />
            <span class="closed-order-span" v-if="!order.isHiring">
              募集終了
            </span>
          </div>
          <div class="order-detail-container">
            <div class="date-weekday">
              <span class="date" data-testid="date">
                {{ date }}
              </span>
              <span
                class="weekday"
                :class="[{ sun: day === 7 }, { sat: day === 6 }]"
                data-testid="weekday"
              >
                {{ dayOfWeek }}
              </span>
            </div>
            <div class="office-hour-container">
              <div class="office-hour" data-testid="office-hour">
                <FeatherIcon icon="clock" size="16" />
                {{ workTime }}
              </div>
            </div>
            <div class="salary-container">
              <div class="salary" data-testid="salary">
                {{ salaryPerDayWithVariousAllowancesSign }}
              </div>
            </div>
          </div>
          <div
            v-if="hasOtherWorkTimeOption"
            class="other-work-time-grid"
            data-testid="hasOtherWorkTimeOption"
          >
            他の勤務時間も選択可能
          </div>
        </div>
        <div class="chevron" v-if="isLink">
          <FeatherIcon icon="chevron-right" size="24" class="chevron-icon" />
        </div>
      </div>
    </div>
  </Component>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { SlimOrder } from "@/types/order/Order";
import { orderService } from "@/app/services/OrderService";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { formatFromISOString, getDay, parseISO } from "@/utils/date";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import Tag from "@/components/atoms/other/Tag.vue";
import { getWorkerServiceById } from "@/const/workerService";

@Component({
  components: {
    FeatherIcon,
    Tag
  }
})
export default class SimpleOrderListItem extends Vue {
  // 直近で応募可能な案件のリスト
  @Prop({
    type: Object,
    required: true
  })
  order!: SlimOrder;

  @Prop({
    type: Object,
    default: true
  })
  isLink!: boolean;

  @Prop({
    type: Object,
    default: true
  })
  isShownTag!: boolean;

  get to() {
    // リンクにしない場合は、自ルートをそのまま返して画面遷移しないようにしておく
    if (!this.isLink) {
      return "";
    }
    return {
      name: "OrdersDetail",
      params: { orderId: this.order.id.toString() }
    };
  }

  get firstWorkTimeOption(): OrderWorkTimeOption {
    return this.order.workTimeOptions[0];
  }

  get salaryPerDayWithVariousAllowancesSign(): string {
    return `¥ ${orderService
      .getSalaryPerDayWithVariousAllowancesFromSlimOrder(
        this.order,
        this.firstWorkTimeOption
      )
      .toLocaleString()}`;
  }

  get day(): number {
    return getDay(parseISO(this.order.date));
  }

  get date(): string {
    // 月/日のフォーマットに変換する
    return formatFromISOString(this.order.date, "MM/dd");
  }

  get isLimited() {
    return orderService.isLimited(this.order);
  }

  get dayOfWeek() {
    return formatFromISOString(this.order.date, "E");
  }

  get workTime() {
    return orderService.getWorkTimeRangeFromSlimOrder(this.firstWorkTimeOption);
  }

  get hasOtherWorkTimeOption() {
    return this.order.workTimeOptions.length > 1;
  }

  get serviceLabel() {
    return getWorkerServiceById(this.order.workerService).displayName;
  }

  get orderTypeLabel() {
    if (this.isLimited) {
      return "限定";
    }
    if (this.order.individualOfferWorker) {
      return "指名";
    }
    return "";
  }
}
</script>

<style lang="scss" scoped>
.order-item-container {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  width: 100%;
  padding: 12px 8px 12px 16px;
  overflow: hidden;
  cursor: pointer;
  background-color: $white;
  border: 1px solid $gray-03;
  border-radius: 8px;

  &.expired {
    background-color: $gray-03 !important;
  }
}

.order-info {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 3px;
}

.small-tag {
  min-width: 36px;
  height: 20px;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
}

.order-limit-and-service-tags {
  display: flex;
  gap: 4px;
  align-items: center;
}

.order-service-tag {
  color: $primary-dark;
  background-color: $gray-07;
}

.order-limit-tag {
  color: $primary-dark;
  background-color: $primary-yellow;
}

.order-detail-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.date-weekday {
  display: flex;
  gap: 2px;
  align-items: center;
  width: 58px;
}

.office-hour-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  justify-content: center;
}

.office-hour {
  display: flex;
  gap: 4px;
  align-items: center;
  height: 22px;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
}

.salary {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $red;
}

.date {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
}

.weekday {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  text-align: center;

  &.sun {
    color: $red;
  }

  &.sat {
    color: $blue;
  }
}

.other-work-time-grid {
  margin-left: 67px;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  color: $gray-05;
}

.salary-container {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: auto;
}

.chevron {
  display: flex;
  align-items: center;
}

.closed-order-span {
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #31383f;
  background-color: #efefef;
  border-radius: 4px;
}
</style>
